import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Avatar,
  CircularProgress,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { forwardRef } from 'react';

import type { ResolvedUser } from '../types';
import type { MentionSuggestionListProps } from './types';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function DefaultSuggestionItem({ user }: { user: ResolvedUser }) {
  // Show email + role + department for better disambiguation
  // Especially important when multiple users have similar names
  const parts = [user.email, user.role?.displayName, user.department].filter(Boolean);

  const secondary = parts.join(' \u00b7 ');

  return (
    <>
      <ListItemAvatar sx={{ minWidth: 40 }}>
        <Avatar
          {...(user.avatar != null ? { src: user.avatar } : {})}
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.light',
            color: 'primary.dark',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {!user.avatar && getInitials(user.fullName)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={user.fullName}
        secondary={secondary || undefined}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
      />
      {user.hasRecordAccess === false && (
        <WarningAmberIcon
          titleAccess="This user does not have access to this record"
          sx={{ color: 'warning.main', fontSize: '1rem', ml: 1, flexShrink: 0 }}
        />
      )}
    </>
  );
}

export const MentionSuggestionList = forwardRef<HTMLDivElement, MentionSuggestionListProps>(
  function MentionSuggestionList(
    {
      items,
      isLoading,
      error,
      selectedIndex,
      onSelect,
      renderItem,
      belowMinQuery,
      minQueryLength,
      sx,
    },
    ref,
  ) {
    if (error) {
      return (
        <Paper
          ref={ref}
          elevation={4}
          sx={[{ minWidth: 280, p: 2 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
          role="listbox"
        >
          <Typography variant="body2" color="error.main" align="center">
            {error}
          </Typography>
        </Paper>
      );
    }

    if (isLoading) {
      return (
        <Paper
          ref={ref}
          elevation={4}
          sx={[{ minWidth: 280, p: 2 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
          role="listbox"
        >
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Searching{'\u2026'}
            </Typography>
          </Stack>
        </Paper>
      );
    }

    if (belowMinQuery) {
      const required = minQueryLength ?? 3;
      return (
        <Paper
          ref={ref}
          elevation={4}
          sx={[{ minWidth: 280, p: 2 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
          role="listbox"
        >
          <Typography variant="body2" color="text.secondary" align="center">
            Type at least {required} character{required === 1 ? '' : 's'} to search
          </Typography>
        </Paper>
      );
    }

    if (items.length === 0) {
      return (
        <Paper
          ref={ref}
          elevation={4}
          sx={[{ minWidth: 280, p: 2 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
          role="listbox"
        >
          <Typography variant="body2" color="text.secondary" align="center">
            No users found
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        ref={ref}
        elevation={4}
        sx={[
          { maxHeight: 280, overflow: 'auto', minWidth: 280 },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <MenuList role="listbox" dense disablePadding>
          {items.map((user, index) => (
            <MenuItem
              key={user.uuid}
              role="option"
              aria-selected={index === selectedIndex}
              selected={index === selectedIndex}
              onClick={() => onSelect(user)}
            >
              {renderItem ? (
                renderItem(user, index === selectedIndex)
              ) : (
                <DefaultSuggestionItem user={user} />
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    );
  },
);
